import { z } from 'zod';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { supportedLanguages, Language } from './translations/types';

dayjs.extend(customParseFormat);

const localizedTextSchema = z.object(
  Object.fromEntries(
    supportedLanguages.map(locale => [locale, z.string().trim().min(1)]),
  ) as Record<Language, z.ZodString>,
);
const assetUrlSchema = z
  .string()
  .url()
  .refine(value => /^https?:\/\//.test(value), 'Expected a hosted asset URL');
const imageVariantsSchema = z.object({ light: assetUrlSchema, dark: assetUrlSchema });

/** The one-shot player these sounds go through only decodes WAV. */
const soundUrlSchema = assetUrlSchema.refine(
  value => /\.wav($|\?)/i.test(value),
  'Event sounds have to be .wav',
);

/** Three recordings, not three volumes: iOS system sounds have no volume control. */
const soundVariantsSchema = z.object({
  low: soundUrlSchema,
  normal: soundUrlSchema,
  high: soundUrlSchema,
});
export const accentColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, 'Use a six-digit hex color');

/**
 * A local wall-clock date and time, `YYYY-MM-DDTHH:mm` — deliberately carries no
 * zone or offset. An event starting at 18:00 starts at 18:00 wherever the user
 * is; a single UTC instant would land on the wrong local day for a third of the
 * world. Seconds are filled in so stored values can be ordered with a plain
 * string comparison.
 */
const localDateTimeSchema = z
  .string()
  .refine(
    value => dayjs(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true).isValid(),
    'Use a real local date and time, YYYY-MM-DDTHH:mm',
  )
  .transform(value => (value.length === 16 ? `${value}:00` : value));

export const activeWindowSchema = z
  .object({
    startsAt: localDateTimeSchema,
    /** Exclusive — the theme is already gone *at* this moment. */
    endsAt: localDateTimeSchema,
  })
  .refine(({ startsAt, endsAt }) => startsAt < endsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export const eventPayloadSchema = z.object({
  activeWindow: activeWindowSchema,
  shareables: z
    .array(
      z.object({
        noun: localizedTextSchema,
        pluralNoun: localizedTextSchema,
        weightKg: z.number().finite().positive(),
        imageUrls: imageVariantsSchema,
      }),
    )
    .min(1),
  accentColor: accentColorSchema,
  likeWorkoutImageUrls: imageVariantsSchema,
  restTimerMessage: localizedTextSchema.optional(),
  /** Left out by an event that themes the visuals only. */
  sounds: z.object({ setComplete: soundVariantsSchema, pr: soundVariantsSchema }).optional(),
});

export type EventPayload = z.infer<typeof eventPayloadSchema>;
export type ActiveWindow = EventPayload['activeWindow'];
export type LocalizedText = EventPayload['shareables'][number]['noun'];
export type ImageVariants = EventPayload['likeWorkoutImageUrls'];
export type SoundVariants = NonNullable<EventPayload['sounds']>['setComplete'];

/** `nowLocal` is the device's wall clock, in the same shape the schema normalises to. */
export const isWithinActiveWindow = (window: ActiveWindow, nowLocal: string): boolean =>
  nowLocal >= window.startsAt && nowLocal < window.endsAt;
